import type { Table } from '@tanstack/react-table';

/**
 * Convert a TanStack table's current visible rows into a CSV string, then
 * trigger a download. Honors column visibility and current sorting / filters.
 */
export function exportTableAsCsv<TData>(table: Table<TData>, filename = 'export.csv'): void {
  const visibleColumns = table.getAllLeafColumns().filter((c) => c.getIsVisible() && c.id !== 'select' && c.id !== 'expand');
  const header = visibleColumns.map((c) => csvEscape(headerText(c.columnDef.header) ?? c.id));
  const rows = table.getFilteredRowModel().rows.map((row) =>
    visibleColumns.map((col) => {
      const value = row.getValue(col.id);
      return csvEscape(stringify(value));
    }),
  );
  const csv = [header, ...rows].map((line) => line.join(',')).join('\n');
  triggerDownload(csv, filename);
}

/** Short, unique download name with no report details: `${prefix}-YYMMDD-HHmmss.${ext}`. */
export function stampedFileName(prefix: string, ext = 'csv'): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  const date = `${p(d.getFullYear() % 100)}${p(d.getMonth() + 1)}${p(d.getDate())}`;
  return `${prefix}-${date}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}.${ext}`;
}

export function downloadCsv(lines: unknown[][], filename = 'export.csv'): void {
  triggerDownload(lines.map((line) => line.map((v) => csvEscape(stringify(v))).join(',')).join('\n'), filename);
}

export type ExportField<TData> = [header: string, value: (row: TData) => unknown];

export function visibleExportFields<TData>(
  table: Table<TData>,
  split: Record<string, ExportField<TData>[]> = {},
): ExportField<TData>[] {
  return table.getVisibleLeafColumns().flatMap((c): ExportField<TData>[] => {
    const own = split[c.id];
    if (own) return own;
    const get = c.accessorFn;
    if (!get || c.id === 'select' || c.id === 'expand') return [];
    return [[headerText(c.columnDef.header) ?? c.id, (row) => get(row, 0)]];
  });
}

export function exportLines<TData>(fields: ExportField<TData>[], rows: TData[]): unknown[][] {
  return [fields.map(([header]) => header), ...rows.map((row) => fields.map(([, value]) => value(row) ?? ''))];
}

export async function downloadXlsx(lines: unknown[][], filename: string, sheetName = 'Calls'): Promise<void> {
  const XLSX = await import('xlsx');
  const wb = XLSX.utils.book_new();
  const sheet = XLSX.utils.aoa_to_sheet(lines);
  sheet['!cols'] = (lines[0] || []).map((_, col) => ({
    wch: Math.min(50, lines.reduce((w, line) => Math.max(w, stringify(line[col]).length + 2), 8)),
  }));
  XLSX.utils.book_append_sheet(wb, sheet, sheetName);
  XLSX.writeFile(wb, filename);
}

function headerText(h: unknown): string | undefined {
  return typeof h === 'string' ? h : undefined;
}

function stringify(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value instanceof Date) return value.toISOString();
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function csvEscape(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function triggerDownload(content: string, filename: string): void {
  // BOM so Excel reads UTF-8 (e.g. "·", ₹, Hindi names) instead of ANSI mojibake like "Â·".
  const blob = new Blob(['\uFEFF' + content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
